import React, { useState, useEffect } from 'react';
import { useAlert } from 'hooks';
import AlertModal from 'components/AlertModal';
import { Form, FormGroup, Label, Input, Row, Col, Button } from 'reactstrap';
import { useForm } from 'hooks/useForm';
import config from 'config';
import { Link } from 'react-router-dom';

interface InitialFormData {
  doc: string;
  nombre: string;
  apellido: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface ValidationFormData {
  doc: string;
  email: string;
  code: string;
}

function Registration() {
  const [showAlert, message, alert] = useAlert(false, '');

  const [isLoading, setIsLoading] = useState(false);
  const [isMailSent, setIsMailSent] = useState(false);
  const [isCodeVerified, setIsCodeVerified] = useState(false);

  const [initialValues, handleIVChange] = useForm<InitialFormData>({
    doc: '',
    nombre: '',
    apellido: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [validationValues, handleVVChange] = useForm<ValidationFormData>({
    doc: '',
    email: '',
    code: '',
  });

  const handleInitialSubmit = async () => {
    try {
      setIsLoading(true);

      const res = await fetch(`${ config.baseUrl }/no-matriculates/registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(initialValues),
      });

      const payload = await res.json();

      if (!res.ok || res.status !== 200 || payload.status !== 'ok') {
        if (!payload.code) {
          throw new Error('Se produjo un error al intentar registrar sus datos. Por favor, intente nuevamente');
        }

        switch (payload.code) {
          case 'MAIL_NOT_VALIDATED':
            // Si el usuario ya está registrado pero no validó su mail continuamos al siguiente paso
            break;
          case 'USER_ALREADY_REGISTERED':
            throw new Error('El usuario ya se encuentra registrado. Por favor, inicie sesión para continuar');
          default:
            throw new Error('Se produjo un error al intentar registrar sus datos. Por favor, intente nuevamente');
        }
      }

      setIsMailSent(true);
    } catch (err) {
      console.error(err);
      if (err.message) {
        alert(true, err.message);
      } else {
        alert(true, 'Se produjo un error al intentar registrar sus datos. Por favor, intente nuevamente');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidationSubmit = async () => {
    try {
      setIsLoading(true);
      const res = await fetch(`${ config.baseUrl }/no-matriculates/registration/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(validationValues),
      });

      const payload = await res.json();

      if (!res.ok || res.status !== 200 || payload.status !== 'ok') {
        if (!payload.code) {
          throw new Error('Se produjo un error al intentar validar sus datos. Por favor, intente nuevamente');
        }

        switch (payload.code) {
          case 'USER_ALREADY_VALIDATED':
            // Si el usuario ya está validado continuamos
            break;
          case 'USER_NOT_FOUND':
            // eslint-disable-next-line max-len
            throw new Error('El usuario no se encuentra registrado. Por favor, verifique que los datos ingresados sean correctos');
          case 'INVALID_CODE':
            // eslint-disable-next-line max-len
            throw new Error('El código ingresado no es válido. Por favor, verifique que el código ingresado sea correcto');
          default:
            throw new Error('Se produjo un error al intentar validar sus datos. Por favor, intente nuevamente');
        }
      }

      setIsCodeVerified(true);
    } catch (err) {
      console.error(err);
      if (err.message) {
        alert(true, err.message);
      } else {
        alert(true, 'Se produjo un error al intentar registrar sus datos. Por favor, intente nuevamente');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mail-sent') == '1') {
      setIsMailSent(true);
    }
  }, []);

  return (
    <>
      <AlertModal isOpen={ showAlert } message={ message } onClose={ () => alert(false) } />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        width: '640px',
      }}>
        <img src={ require('../../logo-40.png') } alt="COKIBA" />
        {
          !isMailSent && (
            <div>
              <h3>Registro de usuario</h3>
              <p>
                Para poder acceder a la plataforma, debe completar el siguiente formulario.
              </p>
              <p>
                Una vez enviado, recibirá un correo electrónico con un código para verificar la dirección ingresada.
              </p>
              <Form>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="nombre">Nombre</Label>
                      <Input
                        type="text"
                        name="nombre"
                        id="nombre"
                        placeholder="Ingrese su nombre"
                        value={ initialValues.nombre }
                        onChange={ handleIVChange }
                        disabled={ isLoading }
                        autoComplete="given-name"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="apellido">Apellido</Label>
                      <Input
                        type="text"
                        name="apellido"
                        id="apellido"
                        placeholder="Ingrese su apellido"
                        value={ initialValues.apellido }
                        onChange={ handleIVChange }
                        disabled={ isLoading }
                        autoComplete="family-name"
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="email">Correo electrónico</Label>
                      <Input
                        type="email"
                        name="email"
                        id="email"
                        placeholder="Ingrese su correo electrónico"
                        value={ initialValues.email }
                        onChange={ handleIVChange }
                        disabled={ isLoading }
                        autoComplete="email"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="doc">DNI</Label>
                      <Input
                        name="doc"
                        id="doc"
                        placeholder="Ingrese su DNI"
                        value={ initialValues.doc }
                        onChange={ handleIVChange }
                        disabled={ isLoading }
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="password">Contraseña</Label>
                      <Input
                        type="password"
                        name="password"
                        id="password"
                        placeholder="Ingrese su contraseña"
                        value={ initialValues.password }
                        onChange={ handleIVChange }
                        disabled={ isLoading }
                        autoComplete="new-password"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="confirmPassword">Confirmar contraseña</Label>
                      <Input
                        type="password"
                        name="confirmPassword"
                        id="confirmPassword"
                        placeholder="Ingrese nuevamente su contraseña"
                        value={ initialValues.confirmPassword }
                        onChange={ handleIVChange }
                        disabled={ isLoading }
                        autoComplete="new-password"
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Button
                    color="primary"
                    onClick={ handleInitialSubmit }
                    disabled={ isLoading }
                  >
                    {
                      isLoading
                        ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        : <>Iniciar Registro</>
                    }
                  </Button>
                </Row>
              </Form>
            </div>
          )
        }
        {
          isMailSent && !isCodeVerified && (
            <div>
              <h3>Registro de usuario</h3>
              <p>
                Para poder acceder a la plataforma, debe validar su correo electrónico.
              </p>
              <Form>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="email">Correo electrónico</Label>
                      <Input
                        type="email"
                        name="email"
                        id="email"
                        placeholder="Ingrese su correo electrónico"
                        value={ validationValues.email }
                        onChange={ handleVVChange }
                        disabled={ isLoading }
                        autoComplete="email"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="doc">DNI</Label>
                      <Input
                        name="doc"
                        id="doc"
                        placeholder="Ingrese su DNI"
                        value={ validationValues.doc }
                        onChange={ handleVVChange }
                        disabled={ isLoading }
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroup>
                      <Label for="code">Código</Label>
                      <Input
                        inputMode="numeric"
                        type="number"
                        name="code"
                        id="code"
                        placeholder="Ingrese su código"
                        value={ validationValues.code }
                        onChange={ handleVVChange }
                        disabled={ isLoading }
                        autoComplete="one-time-code"
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Button
                    color="primary"
                    onClick={ handleValidationSubmit }
                    disabled={ isLoading }
                  >
                    {
                      isLoading
                        ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        : <>Validar Correo Electrónico</>
                    }
                  </Button>
                </Row>
              </Form>
            </div>
          )
        }
        {
          isCodeVerified && (
            <div>
              <h3>Registro de usuario</h3>
              <p>Correo electrónico validado</p>
              <Link to="/login">Ingresar</Link>
            </div>
          )
        }
      </div>
    </>
  );
}

export default Registration;
